// frontend\src\components\Requirements\RequirementProjection.js

import React, { useState, useEffect } from 'react';
import { Table, Card, Alert, Spinner, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { getBudgetProjection } from '../../services/RequirementService';
import { formatDate } from '../../utils/dateUtils';
import ShadowBoxTable from '../ShadowBoxTable';

const RequirementProjection = ({ refreshTrigger }) => {
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState(3);

  useEffect(() => {
    fetchProjections();
  }, [refreshTrigger, months]);

  const fetchProjections = async () => {
    setLoading(true);
    try {
      const data = await getBudgetProjection(months);
      setProjections(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching budget projections:', err);
      setError('Failed to load budget projections. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading && projections.length === 0) {
    return <div className="text-center p-5"><Spinner animation="border" /></div>;
  }

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="section-heading-container">
        <h2 className="section-heading-inline">
          <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "10px" }} />
          Budget Projection
        </h2>
      </div>

      <div className="d-flex justify-content-between mb-4">
        <Form.Group style={{ width: '200px' }}>
          <Form.Label>Projection Period (months)</Form.Label>
          <Form.Select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value))}
          >
            <option value="1">1 month</option>
            <option value="2">2 months</option>
            <option value="3">3 months</option>
            <option value="6">6 months</option>
            <option value="12">12 months</option>
          </Form.Select>
        </Form.Group>

        <Button
          variant="outline-secondary"
          onClick={fetchProjections}
        >
          <FontAwesomeIcon icon={faSync} className="me-2" />
          Refresh
        </Button>
      </div>

      {projections.length === 0 ? (
        <Alert variant="info">
          No budget projections available. Add some recurring requirements to see future projections.
        </Alert>
      ) : (
        projections.map((period, index) => (
          <Card className="mb-4" key={index}>
            <Card.Header>
              <h5 className="mb-0">
                {formatDate(period.period_start)} to {formatDate(period.period_end)}
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-3">
                <h6>Total for period: {formatCurrency(period.total_amount)}</h6>
              </div>

              {period.requirements.length > 0 ? (
                <ShadowBoxTable>
                  <Table striped hover responsive style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Amount</th>
                        <th>Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {period.requirements.map((req, reqIndex) => (
                        <tr key={reqIndex} className="bill-row">
                          <td>{req.name}</td>
                          <td>{formatCurrency(req.amount)}</td>
                          <td>{formatDate(req.due_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ShadowBoxTable>
              ) : (
                <p className="text-center text-muted">No requirements due in this period</p>
              )}
            </Card.Body>
          </Card>
        ))
      )}
    </div>
  );
};

export default RequirementProjection;
